<script setup lang="ts">
const router = useRouter();
const route = useRoute();

useHead({
  title: 'How to integrate Chargebee with ZapScale?',
  meta: [
    {
      name: 'description',
      content:
        'Chargebee integration with ZapScale provides a financial lens into your customer success initiatives, allowing your CS team to focus on revenue health and identify opportunities to improve customer lifetime value.',
    },
    {
      property: 'og:description',
      content:
        'Chargebee integration with ZapScale provides a financial lens into your customer success initiatives, allowing your CS team to focus on revenue health and identify opportunities to improve customer lifetime value.',
    },
    {
      property: 'og:title',
      content: 'How to integrate Chargebee with ZapScale?',
    },
    {
      property: 'og:url',
      content: 'https://www.zapscale.com' + route.path,
    },
    {
      name: 'twitter:card',
      content: 'summary',
    },
  ],
  link: [
    {
      rel: 'canonical',
      href: 'https://www.zapscale.com' + route.path,
    },
  ],
});

useJsonld(() => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'ZapScale',
  description:
    'A single-source customer success management SaaS platform with 50 pre-built playbooks, 40 out-of-the-box KPIs, and unlimited user logins.',
  url: 'https://www.zapscale.com/',
  logo: 'https://www.zapscale.com/images/Header_logo.svg',
  sameAs: [
    'https://www.facebook.com/zapscale',
    'https://www.linkedin.com/company/zapscale/',
    'https://twitter.com/ZapScale',
  ],
}));

const tabList = [
  'All',
  'CRM',
  'Ticketing and Features',
  'Subscription and Payments',
  'Communication',
  'Product Analytics',
  'Data Warehouse',
];

const selectedTab = ref(0);

function onTabClick(index: number) {
  selectedTab.value = index;
  switch (index) {
    case 0:
      router.push({ path: '/integrations', hash: '' });
      break;
    case 1:
      router.push({ path: '/integrations', hash: '#crm' });
      break;
    case 2:
      router.push({ path: '/integrations', hash: '#ticketing-and-features' });
      break;
    case 3:
      router.push({ path: '/integrations', hash: '#subscription-and-payments' });
      break;
    case 4:
      router.push({ path: '/integrations', hash: '#communication' });
      break;
    case 5:
      router.push({ path: '/integrations', hash: '#product-analytics' });
      break;
    case 6:
      router.push({ path: '/integrations', hash: '#data-warehouse' });
      break;
    default:
      break;
  }
}
</script>

<template>
  <div class="bg-[#EFF7FD]">
    <div class="container mx-auto">
      <div class="flex flex-col gap-y-3 px-5 py-6 md:gap-y-12 lg:py-12">
        <div
          class="no-scrollbar mx-auto flex h-10 w-full flex-row items-center rounded-full bg-white px-4 max-md:overflow-x-scroll max-md:whitespace-nowrap md:h-[51px] md:w-fit md:justify-center"
        >
          <div
            v-for="(tab, index) in tabList"
            :key="tab"
            :id="index.toString()"
            class="relative flex h-full cursor-pointer items-center"
            @click="onTabClick(index)"
          >
            <p
              class="px-4 leading-[19.81px] text-[#0070C0] md:text-xs lg:text-[17px]"
              :class="[
                index !== tabList.length - 1 && 'border-r-[1px] border-r-[#0070C0]',
                index === selectedTab && 'font-bold',
              ]"
            >
              {{ tab }}
            </p>
            <div
              v-if="index === selectedTab"
              class="absolute bottom-0 left-4 right-4 mx-auto h-[1px] min-w-max border-b-[1px] border-b-[#0070C0]"
            ></div>
          </div>
        </div>
        <div class="mx-auto flex w-[90%] flex-col gap-y-3 text-left md:w-4/5 md:gap-y-4">
          <h1 class="font-merriweather text-2xl font-extrabold text-[#0070C0] md:text-[25px]">
            ZapScale's Integration with Chargebee
          </h1>
          <p class="text-base font-normal text-[#3A3A3C] md:text-xl">
            Integrate Chargebee, the subscription & billing platform with ZapScale to unlock a wealth of data about your
            customer's subscription health and financial activities. Here's a breakdown of the key information ZapScale
            can gather from Chargebee:
          </p>
          <ul class="list-disc space-y-4 pl-4 text-base font-normal text-[#3A3A3C] md:pl-6 md:text-xl">
            <li>
              <span class="font-semibold">Customer Data:</span>
              <ul class="list-disc space-y-3 pl-6 pt-4 md:pl-12">
                <li>
                  <span class="font-semibold">Subscription Details:</span> This includes information like subscription
                  plan, start date, current status (active/paused/canceled), and any relevant billing details.
                </li>
              </ul>
            </li>
            <li>
              <span class="font-semibold">Financial Data:</span>
              <ul class="list-disc space-y-3 pl-6 pt-4 md:pl-12">
                <li>
                  <span class="font-semibold">Payment Information:</span> Securely store and access payment methods
                  associated with the subscription, but ensure compliance with data security regulations.
                </li>
                <li>
                  <span class="font-semibold">Invoice History:</span> Gain a complete view of past invoices, including
                  invoice dates, amounts, and payment status.
                </li>
                <li>
                  <span class="font-semibold">Payment Activity:</span> Track payment attempts, successes, and failures.
                  This can help identify potential payment issues and prevent involuntary churn.
                </li>
              </ul>
            </li>
          </ul>
        </div>
        <div class="mx-auto flex w-4/5 flex-col gap-y-3 text-left md:gap-y-4">
          <h2 class="font-merriweather text-2xl font-extrabold text-[#0070C0] md:text-[25px]">
            Benefits of Chargebee Integration with ZapScale
          </h2>
          <p class="text-base font-normal text-[#3A3A3C] md:text-xl">
            By integrating Chargebee with ZapScale, you gain valuable financial insights that empower your CS team to:
          </p>
          <ul class="list-disc space-y-3 pl-4 text-base font-normal text-[#3A3A3C] md:pl-6 md:text-xl">
            <li>
              <span class="font-semibold">Proactive Revenue Protection:</span> Identify customers at risk of churn due
              to upcoming renewals or payment failures. This allows for proactive outreach, tailored retention
              strategies, and potentially win-back efforts.
            </li>
            <li>
              <span class="font-semibold">Improved Customer Experience:</span> Address payment issues promptly and
              ensure a smooth subscription experience.
            </li>
            <li>
              <span class="font-semibold">Identify Upsell & Cross-Sell Opportunities:</span> Analyze subscription data
              to identify customers who might benefit from upgrades or additional services based on their current plan
              and usage.
            </li>
            <li>
              <span class="font-semibold">Improved Financial Reporting:</span> Consolidate subscription and financial
              data within ZapScale for better reporting and forecasting.
            </li>
          </ul>
          <p class="text-base font-normal text-[#3A3A3C] md:text-xl">
            Overall, a Chargebee integration provides a financial lens into your customer success initiatives. This
            allows your CS team to focus on revenue health, identify opportunities to improve customer lifetime value,
            and ultimately drive sustainable growth.
          </p>
        </div>
        <div
          class="no-scrollbar mx-auto flex h-10 w-full flex-row items-center rounded-full bg-white px-4 max-md:overflow-x-scroll max-md:whitespace-nowrap md:h-[51px] md:w-fit md:justify-center"
        >
          <div
            v-for="(tab, index) in tabList"
            :key="tab"
            :id="index.toString()"
            class="relative flex h-full cursor-pointer items-center"
            @click="onTabClick(index)"
          >
            <p
              class="px-4 leading-[19.81px] text-[#0070C0] md:text-xs lg:text-[17px]"
              :class="[
                index !== tabList.length - 1 && 'border-r-[1px] border-r-[#0070C0]',
                index === selectedTab && 'font-bold',
              ]"
            >
              {{ tab }}
            </p>
            <div
              v-if="index === selectedTab"
              class="absolute bottom-0 left-4 right-4 mx-auto h-[1px] min-w-max border-b-[1px] border-b-[#0070C0]"
            ></div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="bg-[#FFC52F]">
    <div class="container mx-auto">
      <div class="px-5 py-10 md:px-14">
        <span
          class="mx-auto mb-6 block text-center font-merriweather text-2xl font-bold text-black md:mb-8 xl:w-[971px] xl:text-3xl xl:leading-[50px]"
        >
          Want to know more about how ZapScale seamless integrates<br class="hidden lg:block" />
          with your favorite tool?
        </span>
        <NuxtLink
          to="/demo/#book-a-demo"
          class="mx-auto block h-[41px] w-[179px] rounded-full bg-[#FF395B] px-6 py-2 text-base font-semibold text-white hover:border hover:border-[#FF395B] hover:bg-white hover:text-[#FF395B]"
        >
          <span class="flex items-center justify-between">
            Talk to us
            <Icon name="solar:arrow-right-linear" />
          </span>
        </NuxtLink>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
